import React, { useEffect, useState } from 'react';
import {BiTrash, BiCheck, BiChevronRight, BiChevronLeft, BiPackage} from "react-icons/bi";
import {IoCheckmarkCircle, IoCheckmarkCircleOutline, IoWarningOutline} from "react-icons/io5";
import {HiOutlineLocationMarker} from "react-icons/hi";
import {BsCreditCard, BsTruck} from "react-icons/bs";
import { AiOutlinePlus } from 'react-icons/ai';
import CartService from '../../Utils/API/cart';
import { toast } from 'react-toastify';
import AddressService from '../../Utils/API/address';
import OrderService from '../../Utils/API/orders';
import ReactGA from 'react-ga4';
import OptimizedImage from '../../Components/OptimizedImage';
import AuthService from '../../AuthService';

const API_URL = 'https://api.rb.beeapp.es';

export default function Checkout() {

    const [products, setProducts] = useState([]);
    const [step, setStep] = useState(1); // 1: Carrito, 2: Dirección, 3: Pago
    const [cartID, setCartID] = useState(null);
    const [address, setAddress] = useState([])
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [dropshipping, setDropshipping] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState(0);
    const [loadingOrder, setLoadingOrder] = useState(false);
    const [recommendations, setRecommendations] = useState([]);
    const [loadingRecommendations, setLoadingRecommendations] = useState(false);
    const [addingRecommendation, setAddingRecommendation] = useState({});

    const normalizeProductId = (value) => {
        if (!value) return null;
        if (typeof value === 'object') {
            if (value._id) return value._id.toString();
            if (value.productID) return value.productID.toString();
            return value.toString();
        }
        return value.toString();
    };

    const fetchCartRecommendations = async (cartItems = products) => {
        const token = AuthService.getToken();
        if (!token) {
            setRecommendations([]);
            return;
        }

        setLoadingRecommendations(true);
        try {
            const response = await fetch(`${API_URL}/recommendations/cart`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({}),
            });

            if (!response.ok) {
                throw new Error('No se pudieron cargar recomendaciones');
            }

            const data = await response.json();
            const cartProductIds = new Set(
                (cartItems || [])
                    .map((item) => normalizeProductId(item.productID))
                    .filter(Boolean)
            );

            const incomingRecommendations = Array.isArray(data?.recommendations)
                ? data.recommendations
                : [];

            const filteredRecommendations = incomingRecommendations.filter((recommendation) => {
                const recId = normalizeProductId(recommendation?.productId);
                return recId && !cartProductIds.has(recId);
            });

            setRecommendations(filteredRecommendations);
        } catch (error) {
            console.error('Error cargando recomendaciones de checkout:', error);
            setRecommendations([]);
        } finally {
            setLoadingRecommendations(false);
        }
    };

    const refreshCartData = async () => {
        const cart = await CartService.getCart();
        const cartItems = cart?.items || [];
        setProducts(cartItems);
        setCartID(cart?._id || null);
        return cartItems;
    };

    const addRecommendationToCart = async (recommendation) => {
        const recommendationId = normalizeProductId(recommendation?.productId);
        if (!recommendationId) return;

        setAddingRecommendation((prev) => ({ ...prev, [recommendationId]: true }));
        try {
            const quantityToAdd = recommendation?.product?.quantityStep || 1;
            const result = await CartService.addProductToCart({
                id: recommendationId,
                quantity: quantityToAdd,
            });

            if (!result?.success) return;

            const updatedItems = await refreshCartData();
            await fetchCartRecommendations(updatedItems);
        } catch (error) {
            console.error('Error añadiendo recomendación:', error);
        } finally {
            setAddingRecommendation((prev) => ({ ...prev, [recommendationId]: false }));
        }
    };

    useEffect(() => {
        setLoading(true);
        const getProducts = async () => {
            const products = await CartService.getCart();
            const cartItems = products?.items || [];
            setProducts(cartItems);
            setCartID(products?._id || null);
            await fetchCartRecommendations(cartItems);
            setLoading(false);
        }

        ReactGA.event({
            name: "view_cart",
            currency: "EUR",
            category: "ecommerce",
            action: "view_cart",
        });

        const getAddress = async () => {
            const address = await AddressService.getAddresses();
            if(address.length > 0){
                setSelectedAddress(address[0]._id);
            }
            setAddress(address);
        }
        const getDropshipping = async () => {
            const dropshipping = await AddressService.getDropshippingAddresses();
            setDropshipping(dropshipping);
        }
        getDropshipping();
        getProducts();
        getAddress();
    }, []);

    function addQuantity(id) {
        const newProducts = products.map(product => {
            if(product._id === id){
                product.quantity = parseInt(product.quantity) + 1*product.quantityStep;
                CartService.addProductQuantity(product.productID, product.quantity);
            }
            return product;
        });
        setProducts(newProducts);
    }

    function removeQuantity(id) {
        const newProducts = products.map(product => {
            if(product._id === id){
                product.quantity = parseInt(product.quantity) - 1*product.quantityStep;
                if(product.quantity < 0){
                    product.quantity = 0;
                }
            }
            return product;
        });
        setProducts(newProducts);
        CartService.addProductQuantity(newProducts.find(product => product._id === id).productID, newProducts.find(product => product._id === id).quantity+"");
    }

    function changeQuantity(id, quantity) {
        const newProducts = products.map(product => {
            if(product._id === id && quantity % product.quantityStep === 0){
                product.quantity = (quantity);
                CartService.addProductQuantity(product.productID, product.quantity);
            }else if(product._id === id && quantity % product.quantityStep !== 0){
                product.quantity = quantity;
            }
            return product;
        });
        setProducts(newProducts);
    }

    function createOrder() {
        setLoadingOrder(true);

        ReactGA.event({
            name: "purchase",
            currency: "EUR",
            category: "ecommerce",
            action: "purchase",
        });

        OrderService.createOrder({
            addressID: selectedAddress,
            dropshipping: dropshipping.filter(item => item._id === selectedAddress).length > 0 ? true : false,
            paymentMethod: paymentMethod,
            cartID: cartID,
        }).then(response => {
            if(response.Status === "Created"){
                toast.success('Pedido realizado');
                setLoadingOrder(false);
                window.location.href='/me/orders';
            }else{
                toast.error(response.message);
                setLoadingOrder(false);
            }
        }).catch(error => {
            console.log(error);
        })
    }

    // Verificar si hay productos con problemas de stock
    const hasStockIssues = products.some(product =>
        product.stockInfo && !product.stockInfo.hasEnoughStock
    );

    const subtotal = products.reduce((accumulator, currentValue) => {
        const productPrice = currentValue.discounts && currentValue.discounts.length > 0
            ? currentValue.discounts.find(discount => currentValue.quantity >= discount.minQuantity)
                ? currentValue.discounts.find(discount => currentValue.quantity >= discount.minQuantity).discountedPrice
                : currentValue.price
            : currentValue.price;
        return accumulator + (productPrice * currentValue.quantity);
    }, 0);

    const shippingCost = subtotal >= 100 ? 0 : 6;
    const total = subtotal + shippingCost;

    return (
        <div style={{ minHeight: '100vh', backgroundColor: 'rgb(249, 250, 251)', paddingBottom: '100px' }}>
            {/* Header sticky */}
            <div style={{ backgroundColor: 'rgb(255, 255, 255)', borderBottom: '1px solid rgb(229, 231, 235)', padding: '20px 24px', position: 'sticky', top: 0, zIndex: 100 }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h1 style={{ fontSize: '24px', fontWeight: '700', color: 'rgb(17, 24, 39)', margin: 0, fontFamily: 'Inter, sans-serif' }}>
                        Finalizar compra
                    </h1>
                    <div style={{ display: 'flex', gap: '32px', alignItems: 'center' }}>
                        {/* Step 1: Carrito */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => setStep(1)}>
                            <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                backgroundColor: step >= 1 ? 'rgb(2, 131, 34)' : 'rgb(229, 231, 235)',
                                color: step >= 1 ? 'rgb(255, 255, 255)' : 'rgb(156, 163, 175)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: '600',
                                fontSize: '14px',
                                transition: '0.3s'
                            }}>
                                {step > 1 ? <BiCheck size={20} /> : <BiPackage size={20} />}
                            </div>
                            <span style={{
                                fontSize: '14px',
                                fontWeight: step >= 1 ? '600' : '400',
                                color: step >= 1 ? 'rgb(2, 131, 34)' : 'rgb(156, 163, 175)',
                                fontFamily: 'Inter, sans-serif'
                            }}>Carrito</span>
                        </div>

                        {/* Step 2: Dirección */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: step >= 2 ? 'pointer' : 'default' }} onClick={() => step >= 2 && setStep(2)}>
                            <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                backgroundColor: step >= 2 ? 'rgb(2, 131, 34)' : 'rgb(229, 231, 235)',
                                color: step >= 2 ? 'rgb(255, 255, 255)' : 'rgb(156, 163, 175)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: '600',
                                fontSize: '14px',
                                transition: '0.3s'
                            }}>
                                {step > 2 ? <BiCheck size={20} /> : <HiOutlineLocationMarker size={20} />}
                            </div>
                            <span style={{
                                fontSize: '14px',
                                fontWeight: step >= 2 ? '600' : '400',
                                color: step >= 2 ? 'rgb(2, 131, 34)' : 'rgb(156, 163, 175)',
                                fontFamily: 'Inter, sans-serif'
                            }}>Dirección</span>
                        </div>

                        {/* Step 3: Pago */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: step >= 3 ? 'pointer' : 'default' }} onClick={() => step >= 3 && setStep(3)}>
                            <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                backgroundColor: step >= 3 ? 'rgb(2, 131, 34)' : 'rgb(229, 231, 235)',
                                color: step >= 3 ? 'rgb(255, 255, 255)' : 'rgb(156, 163, 175)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: '600',
                                fontSize: '14px',
                                transition: '0.3s'
                            }}>
                                <BsCreditCard size={20} />
                            </div>
                            <span style={{
                                fontSize: '14px',
                                fontWeight: step >= 3 ? '600' : '400',
                                color: step >= 3 ? 'rgb(2, 131, 34)' : 'rgb(156, 163, 175)',
                                fontFamily: 'Inter, sans-serif'
                            }}>Pago</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Contenido principal */}
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px', display: 'grid', gridTemplateColumns: '1fr 400px', gap: '32px' }}>
                {/* Columna izquierda - Contenido del paso */}
                <div>
                    {step === 1 && (
                        <div>
                            <h2 style={{ fontSize: '20px', fontWeight: '600', color: 'rgb(17, 24, 39)', marginBottom: '20px', fontFamily: 'Inter, sans-serif' }}>
                                Productos en tu carrito ({products.length})
                            </h2>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {products.map(product => {
                                    const hasStockWarning = product.stockInfo && product.stockInfo.warnings && product.stockInfo.warnings.length > 0;
                                    const hasNoStock = product.stockInfo && !product.stockInfo.hasEnoughStock;

                                    return (
                                        <div key={product._id} style={{
                                            backgroundColor: 'rgb(255, 255, 255)',
                                            border: hasNoStock ? '2px solid rgb(239, 68, 68)' : '1px solid rgb(229, 231, 235)',
                                            borderRadius: '12px',
                                            padding: '20px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '16px',
                                            transition: '0.2s'
                                        }}>
                                            <div style={{ display: 'flex', gap: '20px' }}>
                                                <OptimizedImage
                                                    src={product?.images[0] || 'https://cdn.beeapp.es/distribucionesrb/NoImage.png'}
                                                    alt={product?.name}
                                                    width={120}
                                                    height={120}
                                                    fit="contain"
                                                    quality={75}
                                                    style={{
                                                        width: '120px',
                                                        height: '120px',
                                                        objectFit: 'cover',
                                                        borderRadius: '8px',
                                                        border: '1px solid rgb(229, 231, 235)',
                                                        cursor: 'pointer',
                                                        flexShrink: 0
                                                    }}
                                                    onClick={() => window.location.href='/details/'+product?.productURL}
                                                />
                                                <div style={{ flex: '1', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                                    <div>
                                                        <h3
                                                            style={{
                                                                fontSize: '16px',
                                                                fontWeight: '600',
                                                                color: 'rgb(17, 24, 39)',
                                                                margin: '0 0 8px',
                                                                fontFamily: 'Inter, sans-serif',
                                                                cursor: 'pointer'
                                                            }}
                                                            onClick={() => window.location.href='/details/'+product?.productURL}
                                                        >
                                                            {product?.name}
                                                        </h3>
                                                        <p style={{ fontSize: '20px', fontWeight: '700', color: 'rgb(2, 131, 34)', margin: 0, fontFamily: 'Inter, sans-serif' }}>
                                                            {product?.discounts.length > 0 ? (
                                                                product?.discounts.map(discount => {
                                                                    if(product?.quantity >= discount.minQuantity){
                                                                        return discount.discountedPrice;
                                                                    }else{
                                                                        return product?.price;
                                                                    }
                                                                })
                                                            ) : (
                                                                product?.price
                                                            )}€
                                                        </p>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', border: '1px solid rgb(229, 231, 235)', borderRadius: '8px', overflow: 'hidden' }}>
                                                            <button
                                                                onClick={() => removeQuantity(product?._id)}
                                                                style={{
                                                                    width: '36px',
                                                                    height: '36px',
                                                                    border: 'none',
                                                                    backgroundColor: 'rgb(249, 250, 251)',
                                                                    cursor: 'pointer',
                                                                    fontSize: '18px',
                                                                    fontWeight: '600',
                                                                    color: 'rgb(2, 131, 34)',
                                                                    transition: '0.2s'
                                                                }}
                                                            >-</button>
                                                            <input
                                                                type="number"
                                                                value={product?.quantity}
                                                                onChange={(e) => changeQuantity(product?._id, e.target.value)}
                                                                style={{
                                                                    width: '60px',
                                                                    height: '36px',
                                                                    border: 'none',
                                                                    textAlign: 'center',
                                                                    fontSize: '14px',
                                                                    fontWeight: '600',
                                                                    fontFamily: 'Inter, sans-serif',
                                                                    outline: 'none'
                                                                }}
                                                            />
                                                            <button
                                                                onClick={() => addQuantity(product?._id)}
                                                                style={{
                                                                    width: '36px',
                                                                    height: '36px',
                                                                    border: 'none',
                                                                    backgroundColor: 'rgb(249, 250, 251)',
                                                                    cursor: 'pointer',
                                                                    fontSize: '18px',
                                                                    fontWeight: '600',
                                                                    color: 'rgb(2, 131, 34)',
                                                                    transition: '0.2s'
                                                                }}
                                                            >+</button>
                                                        </div>
                                                        <button
                                                            onClick={async () => {
                                                                const productIdToDelete = typeof product?.productID === 'object' ? product?.productID?._id || product?._id : product?.productID;
                                                                const result = await CartService.deleteProductFromCart(productIdToDelete);
                                                                if (result?.success) {
                                                                    const updatedItems = products.filter(product2 => product2._id !== product._id);
                                                                    setProducts(updatedItems);
                                                                    await fetchCartRecommendations(updatedItems);
                                                                }
                                                            }}
                                                            style={{
                                                                padding: '8px',
                                                                backgroundColor: 'rgb(255, 255, 255)',
                                                                border: '1px solid rgb(229, 231, 235)',
                                                                borderRadius: '8px',
                                                                cursor: 'pointer',
                                                                transition: '0.2s',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center'
                                                            }}
                                                        >
                                                            <BiTrash size={20} color="#dc2626" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Advertencias de stock */}
                                            {hasStockWarning && (
                                                <div style={{
                                                    backgroundColor: hasNoStock ? 'rgb(254, 242, 242)' : 'rgb(254, 252, 232)',
                                                    border: hasNoStock ? '1px solid rgb(254, 202, 202)' : '1px solid rgb(254, 240, 138)',
                                                    borderRadius: '8px',
                                                    padding: '12px',
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    gap: '12px'
                                                }}>
                                                    <IoWarningOutline
                                                        size={20}
                                                        color={hasNoStock ? '#dc2626' : '#ca8a04'}
                                                        style={{ flexShrink: 0, marginTop: '2px' }}
                                                    />
                                                    <div style={{ flex: 1 }}>
                                                        {product.stockInfo.warnings.map((warning, index) => (
                                                            <p key={index} style={{
                                                                margin: index === 0 ? '0 0 4px' : '4px 0',
                                                                fontSize: '14px',
                                                                fontWeight: '500',
                                                                color: hasNoStock ? 'rgb(185, 28, 28)' : 'rgb(161, 98, 7)',
                                                                fontFamily: 'Inter, sans-serif'
                                                            }}>
                                                                {warning}
                                                            </p>
                                                        ))}
                                                        {hasNoStock && product.stockInfo.available > 0 && (
                                                            <button
                                                                onClick={() => {
                                                                    changeQuantity(product._id, product.stockInfo.available);
                                                                    CartService.addProductQuantity(product.productID, product.stockInfo.available);
                                                                }}
                                                                style={{
                                                                    marginTop: '8px',
                                                                    padding: '6px 12px',
                                                                    backgroundColor: 'rgb(239, 68, 68)',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: '6px',
                                                                    fontSize: '13px',
                                                                    fontWeight: '600',
                                                                    cursor: 'pointer',
                                                                    fontFamily: 'Inter, sans-serif',
                                                                    transition: '0.2s'
                                                                }}
                                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgb(220, 38, 38)'}
                                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgb(239, 68, 68)'}
                                                            >
                                                                Ajustar a {product.stockInfo.available} unidades
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            <div style={{ marginTop: '28px' }}>
                                <div style={{ marginBottom: '16px' }}>
                                    <h3 style={{
                                        fontSize: '18px',
                                        fontWeight: '600',
                                        color: 'rgb(17, 24, 39)',
                                        margin: 0,
                                        fontFamily: 'Inter, sans-serif'
                                    }}>
                                        Te puede interesar para este pedido
                                    </h3>
                                </div>

                                {loadingRecommendations ? (
                                    <div style={{
                                        backgroundColor: 'rgb(255, 255, 255)',
                                        border: '1px solid rgb(229, 231, 235)',
                                        borderRadius: '12px',
                                        padding: '20px',
                                        textAlign: 'center',
                                        color: 'rgb(107, 114, 128)',
                                        fontFamily: 'Inter, sans-serif'
                                    }}>
                                        Cargando recomendaciones...
                                    </div>
                                ) : recommendations.length === 0 ? (
                                    <div style={{
                                        backgroundColor: 'rgb(255, 255, 255)',
                                        border: '1px solid rgb(229, 231, 235)',
                                        borderRadius: '12px',
                                        padding: '20px',
                                        textAlign: 'center',
                                        color: 'rgb(107, 114, 128)',
                                        fontFamily: 'Inter, sans-serif'
                                    }}>
                                        No hay recomendaciones disponibles en este momento.
                                    </div>
                                ) : (
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                                        gap: '14px'
                                    }}>
                                        {recommendations.slice(0, 8).map((recommendation) => {
                                            const recommendationId = normalizeProductId(recommendation?.productId);
                                            const recommendationProduct = recommendation?.product || {};
                                            const recommendationImage = recommendationProduct?.images?.[0] || 'https://cdn.beeapp.es/distribucionesrb/NoImage.png';
                                            const isAdding = Boolean(addingRecommendation[recommendationId]);

                                            return (
                                                <div key={recommendationId} style={{
                                                    backgroundColor: 'rgb(255, 255, 255)',
                                                    border: '1px solid rgb(229, 231, 235)',
                                                    borderRadius: '12px',
                                                    padding: '14px',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '10px'
                                                }}>
                                                    <div style={{ display: 'flex', gap: '10px' }}>
                                                        <OptimizedImage
                                                            src={recommendationImage}
                                                            alt={recommendationProduct?.name}
                                                            width={72}
                                                            height={72}
                                                            fit="cover"
                                                            quality={70}
                                                            style={{
                                                                width: '72px',
                                                                height: '72px',
                                                                borderRadius: '8px',
                                                                border: '1px solid rgb(229, 231, 235)',
                                                                objectFit: 'cover',
                                                                flexShrink: 0,
                                                                cursor: 'pointer'
                                                            }}
                                                            onClick={() => {
                                                                if (recommendationProduct?.productURL) {
                                                                    window.location.href = '/details/' + recommendationProduct.productURL;
                                                                }
                                                            }}
                                                        />
                                                        <div style={{ minWidth: 0 }}>
                                                            <p style={{
                                                                margin: '0 0 6px',
                                                                fontSize: '14px',
                                                                fontWeight: '600',
                                                                color: 'rgb(17, 24, 39)',
                                                                fontFamily: 'Inter, sans-serif',
                                                                lineHeight: 1.25
                                                            }}>
                                                                {recommendationProduct?.name || 'Producto recomendado'}
                                                            </p>
                                                            <p style={{
                                                                margin: '0 0 6px',
                                                                fontSize: '13px',
                                                                fontWeight: '700',
                                                                color: 'rgb(2, 131, 34)',
                                                                fontFamily: 'Inter, sans-serif'
                                                            }}>
                                                                {(recommendationProduct?.price || 0).toFixed(2)}€
                                                            </p>
                                                            <p style={{
                                                                margin: 0,
                                                                fontSize: '12px',
                                                                color: 'rgb(107, 114, 128)',
                                                                fontFamily: 'Inter, sans-serif'
                                                            }}>
                                                                {recommendation?.reason || 'Relacionado con tu carrito'}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <button
                                                        disabled={isAdding}
                                                        onClick={() => addRecommendationToCart(recommendation)}
                                                        style={{
                                                            width: '100%',
                                                            padding: '10px 12px',
                                                            border: 'none',
                                                            borderRadius: '8px',
                                                            backgroundColor: 'rgb(2, 131, 34)',
                                                            color: 'rgb(255, 255, 255)',
                                                            cursor: isAdding ? 'not-allowed' : 'pointer',
                                                            fontSize: '13px',
                                                            fontWeight: '600',
                                                            fontFamily: 'Inter, sans-serif',
                                                            opacity: isAdding ? 0.6 : 1,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            gap: '8px'
                                                        }}
                                                    >
                                                        <AiOutlinePlus size={15} />
                                                        {isAdding ? 'Añadiendo...' : 'Añadir al carrito'}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div>
                            <h2 style={{ fontSize: '20px', fontWeight: '600', color: 'rgb(17, 24, 39)', marginBottom: '20px', fontFamily: 'Inter, sans-serif' }}>
                                Dirección de envío
                            </h2>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '32px' }}>
                                <div
                                    onClick={() => window.location.href='/me/address'}
                                    style={{
                                        backgroundColor: 'rgb(255, 255, 255)',
                                        border: '2px dashed rgb(229, 231, 235)',
                                        borderRadius: '12px',
                                        padding: '24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '12px',
                                        cursor: 'pointer',
                                        transition: '0.2s'
                                    }}
                                >
                                    <AiOutlinePlus size={20} color="#028322" />
                                    <span style={{ fontSize: '14px', fontWeight: '600', color: 'rgb(2, 131, 34)', fontFamily: 'Inter, sans-serif' }}>
                                        Añadir nueva dirección
                                    </span>
                                </div>

                                {address.map((addr, index) => (
                                    <div
                                        key={index}
                                        onClick={() => setSelectedAddress(addr._id)}
                                        style={{
                                            backgroundColor: 'rgb(255, 255, 255)',
                                            border: selectedAddress === addr._id ? '2px solid rgb(2, 131, 34)' : '2px solid rgb(229, 231, 235)',
                                            borderRadius: '12px',
                                            padding: '20px',
                                            cursor: 'pointer',
                                            transition: '0.2s',
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '12px'
                                        }}
                                    >
                                        {selectedAddress === addr._id ? (
                                            <IoCheckmarkCircle size={24} color="#028322" />
                                        ) : (
                                            <IoCheckmarkCircleOutline size={24} color="#d1d5db" />
                                        )}
                                        <div style={{ flex: 1 }}>
                                            <p style={{ fontSize: '15px', fontWeight: '600', color: 'rgb(17, 24, 39)', margin: '0 0 8px', fontFamily: 'Inter, sans-serif' }}>
                                                {addr.Address}
                                            </p>
                                            <p style={{ fontSize: '14px', color: 'rgb(107, 114, 128)', margin: 0, fontFamily: 'Inter, sans-serif' }}>
                                                {addr.City}, {addr.ZIP}, {addr.Province}, {addr.Country}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'rgb(17, 24, 39)', marginBottom: '16px', fontFamily: 'Inter, sans-serif' }}>
                                Dropshipping
                                <span style={{ fontSize: '14px', fontWeight: '400', color: 'rgb(107, 114, 128)', marginLeft: '8px' }}>
                                    (Envío a terceros)
                                </span>
                            </h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <div
                                    onClick={() => window.location.href='/me/dropshipping'}
                                    style={{
                                        backgroundColor: 'rgb(255, 255, 255)',
                                        border: '2px dashed rgb(229, 231, 235)',
                                        borderRadius: '12px',
                                        padding: '24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '12px',
                                        cursor: 'pointer',
                                        transition: '0.2s'
                                    }}
                                >
                                    <AiOutlinePlus size={20} color="#028322" />
                                    <span style={{ fontSize: '14px', fontWeight: '600', color: 'rgb(2, 131, 34)', fontFamily: 'Inter, sans-serif' }}>
                                        Añadir cliente dropshipping
                                    </span>
                                </div>

                                {dropshipping.map((item, index) => (
                                    <div
                                        key={index}
                                        onClick={() => setSelectedAddress(item._id)}
                                        style={{
                                            backgroundColor: 'rgb(255, 255, 255)',
                                            border: selectedAddress === item._id ? '2px solid rgb(2, 131, 34)' : '2px solid rgb(229, 231, 235)',
                                            borderRadius: '12px',
                                            padding: '20px',
                                            cursor: 'pointer',
                                            transition: '0.2s',
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '12px'
                                        }}
                                    >
                                        {selectedAddress === item._id ? (
                                            <IoCheckmarkCircle size={24} color="#028322" />
                                        ) : (
                                            <IoCheckmarkCircleOutline size={24} color="#d1d5db" />
                                        )}
                                        <div>
                                            <p style={{ fontSize: '15px', fontWeight: '600', color: 'rgb(17, 24, 39)', margin: '0 0 4px', fontFamily: 'Inter, sans-serif' }}>
                                                {item.Name} {item.Surname}
                                            </p>
                                            <p style={{ fontSize: '14px', color: 'rgb(107, 114, 128)', margin: '0 0 4px', fontFamily: 'Inter, sans-serif' }}>
                                                {item.Address}, {item.City}, {item.ZIP}
                                            </p>
                                            <p style={{ fontSize: '13px', color: 'rgb(156, 163, 175)', margin: 0, fontFamily: 'Inter, sans-serif' }}>
                                                {item.Email}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div>
                            <h2 style={{ fontSize: '20px', fontWeight: '600', color: 'rgb(17, 24, 39)', marginBottom: '20px', fontFamily: 'Inter, sans-serif' }}>
                                Método de pago
                            </h2>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '32px' }}>
                                <div
                                    onClick={() => setPaymentMethod(0)}
                                    style={{
                                        backgroundColor: 'rgb(255, 255, 255)',
                                        border: paymentMethod === 0 ? '2px solid rgb(2, 131, 34)' : '2px solid rgb(229, 231, 235)',
                                        borderRadius: '12px',
                                        padding: '20px',
                                        cursor: 'pointer',
                                        transition: '0.2s',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px'
                                    }}
                                >
                                    {paymentMethod === 0 ? (
                                        <IoCheckmarkCircle size={24} color="#028322" />
                                    ) : (
                                        <IoCheckmarkCircleOutline size={24} color="#d1d5db" />
                                    )}
                                    <span style={{ fontSize: '15px', fontWeight: '600', color: 'rgb(17, 24, 39)', fontFamily: 'Inter, sans-serif' }}>
                                        Transferencia bancaria
                                    </span>
                                </div>
                                <div
                                    onClick={() => setPaymentMethod(1)}
                                    style={{
                                        backgroundColor: 'rgb(255, 255, 255)',
                                        border: paymentMethod === 1 ? '2px solid rgb(2, 131, 34)' : '2px solid rgb(229, 231, 235)',
                                        borderRadius: '12px',
                                        padding: '20px',
                                        cursor: 'pointer',
                                        transition: '0.2s',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px'
                                    }}
                                >
                                    {paymentMethod === 1 ? (
                                        <IoCheckmarkCircle size={24} color="#028322" />
                                    ) : (
                                        <IoCheckmarkCircleOutline size={24} color="#d1d5db" />
                                    )}
                                    <span style={{ fontSize: '15px', fontWeight: '600', color: 'rgb(17, 24, 39)', fontFamily: 'Inter, sans-serif' }}>
                                        Pago según convenio
                                    </span>
                                </div>
                            </div>

                            <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'rgb(17, 24, 39)', marginBottom: '16px', fontFamily: 'Inter, sans-serif' }}>
                                Información de envío
                            </h3>
                            <div style={{
                                backgroundColor: 'rgb(240, 253, 244)',
                                border: '1px solid rgb(187, 247, 208)',
                                borderRadius: '12px',
                                padding: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px'
                            }}>
                                <BsTruck size={24} color="#028322" />
                                <div>
                                    <p style={{ fontSize: '14px', fontWeight: '600', color: 'rgb(2, 131, 34)', margin: '0 0 4px', fontFamily: 'Inter, sans-serif' }}>
                                        Envío mediante Distribuciones RB
                                    </p>
                                    <p style={{ fontSize: '13px', color: 'rgb(22, 163, 74)', margin: 0, fontFamily: 'Inter, sans-serif' }}>
                                        Gastos de envío: {shippingCost}€
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Columna derecha - Panel de resumen */}
                <div style={{ position: 'sticky', top: '100px', height: 'fit-content' }}>
                    <div style={{
                        backgroundColor: 'rgb(255, 255, 255)',
                        border: '1px solid rgb(229, 231, 235)',
                        borderRadius: '12px',
                        padding: '24px'
                    }}>
                        <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'rgb(17, 24, 39)', marginBottom: '20px', fontFamily: 'Inter, sans-serif' }}>
                            Resumen del pedido
                        </h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: '14px', color: 'rgb(107, 114, 128)', fontFamily: 'Inter, sans-serif' }}>
                                    Subtotal ({products.length} productos)
                                </span>
                                <span style={{ fontSize: '14px', fontWeight: '600', color: 'rgb(17, 24, 39)', fontFamily: 'Inter, sans-serif' }}>
                                    {subtotal.toFixed(2)}€
                                </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: '14px', color: 'rgb(107, 114, 128)', fontFamily: 'Inter, sans-serif' }}>
                                    Envío
                                </span>
                                <span style={{ fontSize: '14px', fontWeight: '600', color: 'rgb(17, 24, 39)', fontFamily: 'Inter, sans-serif' }}>
                                    {shippingCost}€
                                </span>
                            </div>
                            <div style={{ height: '1px', backgroundColor: 'rgb(229, 231, 235)', margin: '8px 0' }}></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: '16px', fontWeight: '600', color: 'rgb(17, 24, 39)', fontFamily: 'Inter, sans-serif' }}>
                                    Total
                                </span>
                                <span style={{ fontSize: '20px', fontWeight: '700', color: 'rgb(2, 131, 34)', fontFamily: 'Inter, sans-serif' }}>
                                    {total.toFixed(2)}€
                                </span>
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <button
                                onClick={() => {
                                    if(step === 1){
                                        if(hasStockIssues){
                                            toast.error('Por favor, resuelve los problemas de stock antes de continuar');
                                            return;
                                        }
                                        setStep(2);
                                    }else if(step === 2){
                                        setStep(3);
                                    }else if(step === 3){
                                        createOrder();
                                    }
                                }}
                                disabled={loadingOrder || (step === 1 && hasStockIssues)}
                                style={{
                                    width: '100%',
                                    padding: '14px',
                                    backgroundColor: (step === 1 && hasStockIssues) ? 'rgb(156, 163, 175)' : 'rgb(2, 131, 34)',
                                    color: 'rgb(255, 255, 255)',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '15px',
                                    fontWeight: '600',
                                    fontFamily: 'Inter, sans-serif',
                                    cursor: (loadingOrder || (step === 1 && hasStockIssues)) ? 'not-allowed' : 'pointer',
                                    transition: '0.2s',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    opacity: (step === 1 && hasStockIssues) ? 0.6 : 1
                                }}
                            >
                                {loadingOrder ? 'Procesando...' : step === 3 ? 'Finalizar pedido' : 'Continuar'}
                                {!loadingOrder && step < 3 && <BiChevronRight size={20} />}
                            </button>
                            {step === 1 && hasStockIssues && (
                                <div style={{
                                    backgroundColor: 'rgb(254, 242, 242)',
                                    border: '1px solid rgb(254, 202, 202)',
                                    borderRadius: '8px',
                                    padding: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <IoWarningOutline size={18} color="#dc2626" style={{ flexShrink: 0 }} />
                                    <p style={{
                                        margin: 0,
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        color: 'rgb(185, 28, 28)',
                                        fontFamily: 'Inter, sans-serif'
                                    }}>
                                        Hay productos con problemas de stock
                                    </p>
                                </div>
                            )}
                            {step > 1 && (
                                <button
                                    onClick={() => setStep(step - 1)}
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        backgroundColor: 'rgb(255, 255, 255)',
                                        color: 'rgb(107, 114, 128)',
                                        border: '1px solid rgb(229, 231, 235)',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        fontFamily: 'Inter, sans-serif',
                                        cursor: 'pointer',
                                        transition: '0.2s',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    <BiChevronLeft size={20} />
                                    Volver
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
